import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import Loading from './components/Loading'
import './App.css'

// Critical routes - loaded immediately (Home page for LCP)
import Home from './pages/Home'

// Lazy loaded routes - loaded on demand for better code splitting and CSS optimization
const KineticTraining = lazy(() => import('./pages/KineticTraining'))
const MedicalTraining = lazy(() => import('./pages/MedicalTraining'))
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'))
const Forum = lazy(() => import('./pages/Forum'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Profile = lazy(() => import('./pages/Profile'))
const Sitemap = lazy(() => import('./pages/Sitemap'))
const Contact = lazy(() => import('./pages/Contact'))
const Support = lazy(() => import('./pages/Support'))
const PolitykaPrywatnosci = lazy(() => import('./pages/PolitykaPrywatnosci'))
const Regulamin = lazy(() => import('./pages/Regulamin'))
const ONas = lazy(() => import('./pages/ONas'))
const DlaFirm = lazy(() => import('./pages/DlaFirm'))
const ComponentsDemoSimple = lazy(() => import('./pages/ComponentsDemoSimple'))
const ComponentsDemoFixed = lazy(() => import('./pages/ComponentsDemoFixed'))
const NewComponentsDemo = lazy(() => import('./pages/NewComponentsDemo'))
const SkeletonDemo = lazy(() => import('./pages/SkeletonDemo'))
const TypographyDemo = lazy(() => import('./pages/TypographyDemo'))
const ModerationPanel = lazy(() => import('./pages/ModerationPanel'))
const AdminPanel = lazy(() => import('./pages/AdminPanel'))
const AdminAuthors = lazy(() => import('./pages/AdminAuthors').then(module => ({ default: module.AdminAuthors })))
const PublicProfile = lazy(() => import('./pages/PublicProfile'))
const Library = lazy(() => import('./pages/Library').then(module => ({ default: module.Library })))
const BookDetails = lazy(() => import('./components/library/BookDetails').then(module => ({ default: module.BookDetails })))
const AddBook = lazy(() => import('./components/library/AddBook').then(module => ({ default: module.AddBook })))
const CreateModuleFromBook = lazy(() => import('./components/library/CreateModuleFromBook').then(module => ({ default: module.CreateModuleFromBook })))
const AuthorProfile = lazy(() => import('./pages/AuthorProfile').then(module => ({ default: module.AuthorProfile })))
const Authors = lazy(() => import('./pages/Authors').then(module => ({ default: module.Authors })))
const EditAuthor = lazy(() => import('./pages/EditAuthor').then(module => ({ default: module.EditAuthor })))
const UserDownloadHistory = lazy(() => import('./components/library/UserDownloadHistory').then(module => ({ default: module.UserDownloadHistory })))
// Events routes
const Events = lazy(() => import('./pages/Events').then(module => ({ default: module.Events })))
const EventDetailsPage = lazy(() => import('./pages/EventDetailsPage').then(module => ({ default: module.EventDetailsPage })))
const CreateEvent = lazy(() => import('./pages/CreateEvent').then(module => ({ default: module.CreateEvent })))
const EditEvent = lazy(() => import('./pages/EditEvent').then(module => ({ default: module.EditEvent })))
// Trainings routes
const Trainings = lazy(() => import('./pages/Trainings').then(module => ({ default: module.Trainings })))
const TrainingDetails = lazy(() => import('./pages/TrainingDetails').then(module => ({ default: module.TrainingDetails })))
const TrainingComparison = lazy(() => import('./pages/TrainingComparison').then(module => ({ default: module.TrainingComparison })))
const TrainingPaymentSuccess = lazy(() => import('./pages/TrainingPaymentSuccess'))
const TrainingPaymentCancelled = lazy(() => import('./pages/TrainingPaymentCancelled'))
const MyTrainingBookings = lazy(() => import('./pages/MyTrainingBookings'))
const PlaceholderPage = lazy(() => import('./pages/PlaceholderPage'))
const StartTraining = lazy(() => import('./pages/StartTraining'))

export default function AppRoutes() {
  return (
    <Routes>
      {/* Login i Register - TERAZ Z LAYOUTEM (wspólna nawigacja) */}
      <Route
        path="/logowanie"
        element={
          <Layout>
            <Login />
          </Layout>
        }
      />
      <Route
        path="/rejestracja"
        element={
          <Layout>
            <Register />
          </Layout>
        }
      />
      <Route
        path="/zmien-haslo"
        element={
          <Layout>
            <ChangePassword />
          </Layout>
        }
      />
      <Route
        path="/resetuj-haslo"
        element={
          <Layout>
            <ResetPassword />
          </Layout>
        }
      />
      <Route
        path="/profil"
        element={
          <Layout>
            <Profile />
          </Layout>
        }
      />

      {/* Redirecty ze starych URL na nowe */}
      <Route path="/login" element={<Navigate to="/logowanie" replace />} />
      <Route path="/zaloguj-sie" element={<Navigate to="/logowanie" replace />} />
      <Route path="/zarejestruj-sie" element={<Navigate to="/rejestracja" replace />} />

      {/* Pozostałe strony - z layoutem */}
      <Route
        path="/"
        element={
          <Layout>
            <Home />
          </Layout>
        }
      />
      <Route
        path="/rozpocznij-szkolenie"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <StartTraining />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Trainings />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia-energokinetyczne"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <KineticTraining />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia-medyczne"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <MedicalTraining />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/forum"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Forum />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/forum/uzytkownik/:userSlug"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PublicProfile />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/forum/watek/:threadSlug"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Forum />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/mapa-strony"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Sitemap />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/mapa-witryny"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Sitemap />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/kontakt"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Contact />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/wsparcie"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Support />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/polityka-prywatnosci"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PolitykaPrywatnosci />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/regulamin"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Regulamin />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/o-nas"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <ONas />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/dla-firm"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <DlaFirm />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/instruktorzy"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/strzelnice"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/blog"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/wideo"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/konto"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/faq"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/domena-informacyjna"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/domena-energetyczna"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/components-demo"
        element={
          <Suspense fallback={<Loading />}>
            <ComponentsDemoFixed />
          </Suspense>
        }
      />
      <Route
        path="/components-demo-simple"
        element={
          <Suspense fallback={<Loading />}>
            <ComponentsDemoSimple />
          </Suspense>
        }
      />
      <Route
        path="/skeleton-demo"
        element={
          <Suspense fallback={<Loading />}>
            <SkeletonDemo />
          </Suspense>
        }
      />
      <Route
        path="/new-components"
        element={
          <Suspense fallback={<Loading />}>
            <NewComponentsDemo />
          </Suspense>
        }
      />
      <Route
        path="/typography-demo"
        element={
          <Suspense fallback={<Loading />}>
            <TypographyDemo />
          </Suspense>
        }
      />
      <Route
        path="/moderacja"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <ModerationPanel />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/panel-zarzadzania"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <AdminPanel />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/panel-zarzadzania/autorzy"
        element={<Navigate to="/panel-zarzadzania?tab=authors" replace />}
      />
      <Route path="/admin" element={<Navigate to="/panel-zarzadzania" replace />} />
      <Route path="/admin/autorzy" element={<Navigate to="/panel-zarzadzania?tab=authors" replace />} />
      <Route
        path="/biblioteka"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Library />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/kossecki"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/mazura"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/domeny-wojny"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/wojna-informacyjna"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/autonomia"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route path="/library" element={<Navigate to="/biblioteka" replace />} />
      <Route
        path="/library/book/:id"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <BookDetails />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/ksiazka/:id"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <BookDetails />
            </Suspense>
          </Layout>
        }
      />
      {/* Authors List - must be before /biblioteka/:slug */}
      <Route
        path="/biblioteka/autorzy"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Authors />
            </Suspense>
          </Layout>
        }
      />
      {/* User Download History */}
      <Route
        path="/biblioteka/moje-pobierania"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <UserDownloadHistory />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/autor/:id/edytuj"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <EditAuthor />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/library/author/:id/edit"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <EditAuthor />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/:slug"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <BookDetails />
            </Suspense>
          </Layout>
        }
      />
      {/* Author Profile */}
      <Route
        path="/autor/:slug"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <AuthorProfile />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/library/add"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <AddBook />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/dodaj"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <AddBook />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/library/edit/:id"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <AddBook />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/edytuj/:id"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <AddBook />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/library/create-module/:bookId"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <CreateModuleFromBook />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/biblioteka/utworz-modul/:bookId"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <CreateModuleFromBook />
            </Suspense>
          </Layout>
        }
      />

      {/* Events routes */}
      <Route
        path="/wydarzenia"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <Events />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/wydarzenia/nowe"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <CreateEvent />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/wydarzenia/edytuj/:id"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <EditEvent />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/wydarzenia/:slug"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <EventDetailsPage />
            </Suspense>
          </Layout>
        }
      />

      {/* Trainings routes */}
      <Route
        path="/szkolenia-taktyczne"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/pakiety"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/community"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <PlaceholderPage />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia/porownaj"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <TrainingComparison />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia/moje"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <MyTrainingBookings />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia/platnosc/sukces"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <TrainingPaymentSuccess />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia/platnosc/anulowana"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <TrainingPaymentCancelled />
            </Suspense>
          </Layout>
        }
      />
      <Route
        path="/szkolenia/:slug"
        element={
          <Layout>
            <Suspense fallback={<Loading />}>
              <TrainingDetails />
            </Suspense>
          </Layout>
        }
      />
    </Routes>
  )
}


